/**
 * The Touch detail rows, fetched only when that page opens.
 *
 * A separate artefact from the bundle (see api/touches): ~340K touches, columnar and
 * dictionary-encoded, scoped server-side to the people the caller may see. Kept
 * columnar in the browser too — a row is an index, never an object.
 */
import { useCachedDetail } from './detailFetch'

type DictKey = 'e' | 'fg' | 'f' | 's' | 'd' | 'wf' | 'wl' | 'p' | 'pn' | 'sd' | 'ad'

/**
 * The claim each touch is on, for the claim basis (see touchRows.ts): `o` its owner and
 * `rm` the month it resolved in, '' unless it is a resolved claim the Touch profile
 * counts; `ci` an integer per such claim, -1 otherwise. Files built before review #397
 * (2026-10-12) carry `bm`, its billed month, in place of `rm`; before 2026-10-02,
 * neither.
 */
type ClaimDictKey = 'o' | 'rm' | 'bm'

/**
 * The patient on the touch's claim, "Last, First"; '' where Medevolve has no name
 * (review #70). PHI: /api/touches rebuilds it from the caller's own rows. Absent until
 * the build can read Medevolve's claim worklist.
 */
type PatientKey = 'pt'

export interface TouchDetailData {
  version: string
  generatedAt: string
  n: number
  dict: Record<DictKey, string[]> & Partial<Record<ClaimDictKey | PatientKey | 'wr', string[]>>
  cols: Record<DictKey, number[]> &
    Partial<Record<ClaimDictKey | PatientKey | 'ci', number[]>> & {
      v: string[]
      note: string[]
      /**
       * The weight of the claim each touch is on, and why (into dict.wr), added by
       * /api/touches (api/shared/claimWeights.ts weighTouches). Absent when the rules
       * or the claim rows could not be read.
       */
      w?: number[]
      wr?: number[]
    }
  /** The rule set's tag, or 'unavailable'. Absent from an older API. */
  weights?: string
}

const TOUCHES_URL = (import.meta.env.VITE_TOUCHES_URL as string | undefined) ?? '/api/touches'

/** Shown from the browser's copy at once on a return visit; see detailFetch.ts. */
export function useTouchDetail(
  /** False holds the download back: Claim detail for a reader without Touch detail. */
  enabled = true,
): { data: TouchDetailData | null; error: string | null } {
  return useCachedDetail<TouchDetailData>('touches', TOUCHES_URL, "Touch rows aren't available yet.", enabled)
}
